import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import cuid from '@paralleldrive/cuid2';
import moment from 'moment';
// self
import './index.less';
import { IGetPhytophagyStressReqModel } from '@/util/Request/agrialert/requestType';
import {
    IAgriAlertTranslateResModel,
    IGetPhytophagyStressRiskDataResModel,
} from '@/util/Request/agrialert/responseType';
import { getWeekTranslate } from '../../initData';
import { lowerCaseFirstLetter } from '@/util/commonFunction';
import {
    agriAlertGetPhytophagyStressData,
    agriAlertGetPhytophagyForstRiskData,
} from '@/util/Request/agrialert';
import IF from '@/components/IF';
import { HeatOrColdStressRiskEnum } from '@/page/Agrialert/template-management/template-management';

import ForecastEchart from './template-stress-chart';
import HistoricalChart from './template-stress-chart';

const HeatColor = {
    LOW: '#FFDCE0',
    Med: '#F6B7B8',
    High: '#ef7878',
    White: 'white',
};
const ColdColor = {
    LOW: '#E9FDFF',
    Med: '#B1EBFF',
    High: '#7bbde7',
    White: 'white',
};

interface IProp {
  showPhytophagyStress: boolean;
  allPhytophagyStressReq: IGetPhytophagyStressReqModel[];
  translateValue: IAgriAlertTranslateResModel;
  languageCode: string;
  heatOrColdType: HeatOrColdStressRiskEnum;
}



const TemplatePhytophagy = (prop: IProp) => {
    const {
        showPhytophagyStress,
        allPhytophagyStressReq,
        translateValue,
        languageCode,
        heatOrColdType,
    } = prop;

    const finalFormat = languageCode === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
    const finalRenderFormat = languageCode === 'fr' ? 'DD/MM' : 'MM/DD';

    const [
        currentPhytophagyStressReqDataForDaily,
        setCurrentPhytophagyStressReqDataForDaily,
    ] = useState<IGetPhytophagyStressRiskDataResModel[][]>([]);
    const [echartData,setEchartData] = useState<IGetPhytophagyStressRiskDataResModel[][]>([]);
    const [forecastEchartData,setForecastEchartData] = useState<IGetPhytophagyStressRiskDataResModel[]>([]);
    const [historicalEchartData,setHistoricalEchartData] = useState<IGetPhytophagyStressRiskDataResModel[]>([]);
    const [mixedEchartData,setMixedEchartData] = useState<IGetPhytophagyStressRiskDataResModel[]>([]);


    const [repeatRequest, setRepeatRequest] = useState<string[]>([]);

    // 使用 useMemo 包裹 reduce 操作，确保只在依赖项变化时执行一次
    const mergedPhytophagyStressReq = useMemo(() => {
        const repeatEntries: string[] = []; // 临时存储重复项

        const result = allPhytophagyStressReq.reduce((acc, current) => {


            const existingGroup = acc.find(
                (item) => item.groupId === current.groupId
            );

            if (existingGroup) {
                const riskList = existingGroup.cropType.split(';');
                if (riskList.includes(current.cropType)) {
                // 收集重复条目
                    const duplicateEntry = `${current.cropType}#${current.startDate}#${current.endDate}`;
                    repeatEntries.push(duplicateEntry);
                } else {
                // 拼接新的 diseaseRisk
                    existingGroup.cropType += `;${current.cropType}`;
                }
            } else {
            // 添加新项
                acc.push({ ...current });
            }

            return acc;
        }, [] as IGetPhytophagyStressReqModel[]);

        // 统一更新 repeatRequest（仅收集到新重复项时才更新）
        setRepeatRequest(repeatEntries);

        return result;
    }, [allPhytophagyStressReq]); // 仅在这两个依赖变化时重新执行
    // const mergedPhytophagyStressReq = allPhytophagyStressReq.reduce(
    //     (acc, current) => {
    //         const existingGroup = acc.find(
    //             (item) => item.groupId === current.groupId
    //         );

    //         if (existingGroup) {
    //             // 如果已经存在相同的 groupId，则拼接 StressAlertReq
    //             existingGroup.cropType += `;${current.cropType}`;
    //         } else {
    //             // 如果不存在相同的 groupId，则添加新的项
    //             acc.push({ ...current });
    //         }

    //         return acc;
    //     },
    // [] as IGetPhytophagyStressReqModel[]
    // );

    // get stress alert data
    const getPhytophagyStressListDataFunc = async () => {
        try {
            // 创建一个数组来存储所有的 promise
            const promises = mergedPhytophagyStressReq?.map(async (item) => {
                return getStressAlertDataFuc(item);
            });

            // 使用 Promise.all 等待所有的 promise 完成
            const results = await Promise.all(promises);
            setCurrentPhytophagyStressReqDataForDaily(results);
            setEchartData(results);

            return results; // 返回所有结果
        } catch (error) {
            console.error('Failed to get stress alert data, error is: ', error);
            return null;
        }
    };

    const getStressAlertDataFuc = async (
        requestModel: IGetPhytophagyStressReqModel
    ) => {
        try {
            let res = null;
            if (heatOrColdType === HeatOrColdStressRiskEnum.HEATSTRESSRISK) {
                res = await agriAlertGetPhytophagyStressData(requestModel);
            } else if (heatOrColdType === HeatOrColdStressRiskEnum.COLDSTRESSRISK) {
                res = await agriAlertGetPhytophagyForstRiskData(requestModel);
            }
            // translate the week name
            // const format = languageCode === 'fr' ? 'DD/MM/YYYY' : 'MM/DD/YYYY';
            if (res?.data?.length >= 1) {
                res?.data?.forEach((item, itemIndex) => {
                    res.data[itemIndex].backColors = [];
                    item?.date?.forEach((dateItem, dateIndex) => {
                        // const dayOfWeek = moment(dateItem, format)?.day();
                        // res.data[itemIndex].date[dateIndex] = dateItem + ' ' + getWeekTranslate(translateValue, dayOfWeek); // set backColor
                        res.data[itemIndex].date[dateIndex] = dateItem; // set backColor
                    });

                    // set for backColor
                    item?.recommendationValues?.forEach(
                        (valueDayGroup, valueDayGroupIndex) => {
                            res.data[itemIndex].backColors[valueDayGroupIndex] = [];
                            valueDayGroup?.forEach((valueItem) => {
                                const tmpColor = getPhytophagyStressColor(
                                    valueItem,
                                    heatOrColdType
                                );
                                res.data[itemIndex].backColors[valueDayGroupIndex].push(
                                    tmpColor
                                );
                            });
                        }
                    );

                    // change for model name translate
                    // for crop name translate
                    let tmpModelId = lowerCaseFirstLetter(item?.cropCode);
                    if (heatOrColdType === HeatOrColdStressRiskEnum.COLDSTRESSRISK) {
                        tmpModelId = mappingColdStressToHeatStressCode(tmpModelId);
                    }
                    res.data[itemIndex].cropName = translateValue?.[tmpModelId];
                });
            }
            return res?.data;
        } catch (error) {
            return null;
            console.log('get disease risk data failed, error is ', error);
        }
    };

    const mappingColdStressToHeatStressCode = (coldStressCode: string) => {
        let tmpResult = null;
        switch (coldStressCode) {
            case 'aSF_Corn':
                tmpResult = 'aS_Corn';
                break;
            case 'aSF_Potatoes':
                tmpResult = 'aS_Potatoes';
                break;
            case 'aSF_WinterCereals':
                tmpResult = 'aS_WinterCereals';
                break;
            case 'aSF_Sunflower':
                tmpResult = 'aS_Sunflower';
                break;
            case 'aSF_Canola':
                tmpResult = 'aS_Canola';
                break;
            case 'aSF_Grapes':
                tmpResult = 'aS_Grapes';
                break;
            default:
                return '0';
                break;
        }
        return tmpResult;
    };

    const getPhytophagyStressColor = (
        value: string,
        type: HeatOrColdStressRiskEnum
    ) => {
        const tmpValue = Number(value) || 0;
        let tmpColor = '';
        if (type === HeatOrColdStressRiskEnum.HEATSTRESSRISK) {
            if (tmpValue > 0 && tmpValue <= 3) {
                // tmpColor = '#FFF2F3';
                tmpColor = HeatColor.LOW;
            } else if (tmpValue > 3 && tmpValue <= 6) {
                tmpColor = HeatColor.Med;
            } else if (tmpValue > 6) {
                tmpColor = HeatColor.High;
            } else {
                tmpColor = HeatColor.White;
            }
        } else if (type === HeatOrColdStressRiskEnum.COLDSTRESSRISK) {
            if (tmpValue > 0 && tmpValue <= 3) {
                tmpColor = ColdColor.LOW;
            } else if (tmpValue > 3 && tmpValue <= 6) {
                tmpColor = ColdColor.Med;
            } else if (tmpValue > 6) {
                tmpColor = ColdColor.High;
            } else {
                tmpColor = ColdColor.White;
            }
        }
        return tmpColor;
    };

    useEffect(() => {
        if (showPhytophagyStress && translateValue) {
            getPhytophagyStressListDataFunc();
        }
    }, [showPhytophagyStress, translateValue]);

    const classifyDataByFirstDate = (data:IGetPhytophagyStressRiskDataResModel[][]) => {
        const currentDate = moment().startOf('day'); // 获取当前日期（去掉时间部分）
        const historicalArray = []; // 存储历史数据
        const futureArray = []; // 存储未来数据

        const mixedData = [];
        // filter for mixed value
        const uniqueGroupIdForMixed = Array.from(new Set(allPhytophagyStressReq?.map(item => item.groupIdForMixed)
            ?.filter((value): value is string => value !== undefined)));


        // for forecast and historical group
        data.forEach((group) => {
            group.forEach((item) => {
                const firstDateStr = item?.originDate?.[0]?.split(' ')[0]; // 提取第一个日期部分
                if (!firstDateStr) {
                    console.error('Invalid date array:', item);
                    return;
                }

                const firstDate = moment(firstDateStr, 'YYYY-MM-DD', true); // 使用严格模式解析日期
                if (!firstDate.isValid()) {
                    console.error('Invalid first date:', firstDateStr, 'in item:', item);
                    return;
                }

                // 判断第一个日期是否小于当前日期
                if (firstDate.isBefore(currentDate, 'day')) {
                    historicalArray.push(item); // 归类为历史数据
                } else {
                    futureArray.push(item); // 归类为未来数据
                }
            });
        });

        repeatRequest.forEach((item) => {
            const [modelId, startDate,endDate] = item.split('#');
            const tmpFutureItem = futureArray.find((futureItem) =>
                futureItem.cropCode === modelId &&
                futureItem.originDate?.[0] === startDate &&
                futureItem.originDate?.[futureItem.originDate?.length - 1] === endDate);
            const tmpHistoricalItem = historicalArray.find((historicalItem) =>
                historicalItem.cropCode === modelId &&
                historicalItem.originDate?.[0] === startDate &&
                historicalItem.originDate?.[historicalItem.originDate?.length - 1] === endDate);
            if(tmpFutureItem){
                futureArray.push(tmpFutureItem);
            }
            if(tmpHistoricalItem){
                historicalArray.push(tmpHistoricalItem);
            }
        });

        const forecastDateArray = mergeAndSortDates(futureArray);
        const historicalDateArray = mergeAndSortDates(historicalArray);

        updateItemsWithFinalDates(futureArray, forecastDateArray);
        updateItemsWithFinalDates(historicalArray, historicalDateArray);

        const mixedFutureIndex = [];
        const mixedHistoricalIndex = [];
        uniqueGroupIdForMixed?.forEach((item) => {
            // const groutKeyData = item?.split('-');
            const [historicalDaysStr, forecastDaysStr, modelId] = item.split('-');
            if (!historicalDaysStr || !forecastDaysStr || !modelId) {
                console.warn(`Invalid groupId format: ${item}`);
                return;
            }
            let tmpFutureItem:IGetPhytophagyStressRiskDataResModel = null;
            let tmpHistoricalItem:IGetPhytophagyStressRiskDataResModel = null;
            // for future
            for (let itemIndex = 0; futureArray && itemIndex < futureArray.length; itemIndex++) {
                const item = futureArray[itemIndex];
                if (item.cropCode === modelId &&
                    item?.date?.length === Number(forecastDaysStr) &&
                    !(mixedFutureIndex?.includes(itemIndex))
                ) {
                    tmpFutureItem = item;
                    mixedFutureIndex.push(itemIndex);
                    break; // 找到后立即退出循环
                }
            }
            // futureArray?.forEach((item, itemIndex) => {
            //     if (item?.cropCode === modelId && item?.date?.length === Number(forecastDaysStr)) {
            //         tmpFutureItem = item;
            //         mixedFutureIndex.push(itemIndex);
            //         return;
            //     }
            // });
            // for historical
            for (let itemIndex = 0; historicalArray && itemIndex < historicalArray.length; itemIndex++) {
                const item = historicalArray[itemIndex];
                if (item.cropCode === modelId &&
                    item?.date?.length === Number(historicalDaysStr) &&
                    !(mixedHistoricalIndex?.includes(itemIndex))
                ) {
                    tmpHistoricalItem = item;
                    mixedHistoricalIndex.push(itemIndex);
                    break; // 找到后立即退出循环
                }
            }
            // historicalArray?.forEach((item, itemIndex) => {
            //     if (item?.cropCode === modelId && item?.date?.length === Number(historicalDaysStr)) {
            //         tmpHistoricalItem = item;
            //         mixedHistoricalIndex.push(itemIndex);
            //         return;
            //     }
            // });

            const tmpMixedItem: IGetPhytophagyStressRiskDataResModel = {
                cropCode: tmpFutureItem?.cropCode || tmpHistoricalItem?.cropCode,
                cropName: tmpFutureItem?.cropName || tmpHistoricalItem?.cropName,
                date: [...tmpHistoricalItem.date, ...tmpFutureItem.date],
                finalDate: [...tmpHistoricalItem.finalDate, ...tmpFutureItem.finalDate],
                recommendationValues: [
                    ...tmpHistoricalItem.recommendationValues,
                    ...tmpFutureItem.recommendationValues,
                ],
                finalRecommendationValues: [
                    ...tmpHistoricalItem.finalRecommendationValues,
                    ...tmpFutureItem.finalRecommendationValues,
                ],
                backColors: [
                    ...tmpHistoricalItem.backColors,
                    ...tmpFutureItem.backColors,
                ],
                finalBackColors: [
                    ...tmpHistoricalItem.finalBackColors,
                    ...tmpFutureItem.finalBackColors,
                ],
            };
            mixedData.push(tmpMixedItem);
        });

        // setForecastEchartData(futureArray);
        // setHistoricalEchartData(historicalArray);
        setForecastEchartData(futureArray?.filter((_, index) => !mixedFutureIndex?.includes(index)) || []);
        setHistoricalEchartData(historicalArray?.filter((_, index) => !mixedHistoricalIndex?.includes(index)) || []);
        setMixedEchartData(mixedData);

        console.log('Historical Data:', historicalArray);
        console.log('Historical Date:', forecastDateArray);
        console.log('Future Data:', futureArray);
        console.log('Future Date:', historicalDateArray);

        return { historicalArray, futureArray };
    };

    const mergeAndSortDates = (historicalArray) => {
        const allDates = new Set(); // 使用Set去重

        // 提取所有日期
        historicalArray.forEach((item) => {
            item.date.forEach((dateStr) => {
                const rawDate = dateStr.split(' ')[0]; // 提取日期部分
                const parsedDate = moment(rawDate, finalFormat, true); // 使用严格模式解析日期

                if (!parsedDate.isValid()) {
                    console.error('Invalid date found:', rawDate);
                    return;
                }

                allDates.add(parsedDate.format(finalFormat)); // 添加到Set中
            });
        });

        // 将Set转换为数组并排序
        const sortedDates = Array.from(allDates)
            .map(date => moment(date, finalFormat))
            .sort((a, b) => a.diff(b))
            .map(date => date.format(finalFormat));

        console.log('Sorted Dates:', sortedDates);

        return sortedDates;
    };

    // 公共方法：更新数据项中的日期、推荐值和背景色
    const updateItemsWithFinalDates = (
        items: IGetPhytophagyStressRiskDataResModel[],
        finalDateArray: string[]
    ) => {
        items.forEach((item) => {
            const finalRecommendationValues = Array(finalDateArray.length).fill('');
            const finalBackColors = Array(finalDateArray.length).fill('');

            item.date.forEach((dateStr: string, index: number) => {
                const rawDate = dateStr.split(' ')[0]; // 提取日期部分
                const parsedDate = moment(rawDate, finalFormat, true); // 使用严格模式解析日期

                if (!parsedDate.isValid()) {
                    console.error('Invalid date found:', rawDate);
                    return;
                }

                const formattedDate = parsedDate.format(finalFormat);
                const forecastIndex = finalDateArray.indexOf(formattedDate); // 查找日期在最终日期数组中的索引

                if (forecastIndex !== -1) {
                    // 将 recommendationValues 和 backColors 填入对应索引
                    if (item.recommendationValues[index] && item.recommendationValues[index][0]) {
                        finalRecommendationValues[forecastIndex] = item.recommendationValues[index][0];
                    }
                    if (item.backColors[index] && item.backColors[index][0]) {
                        finalBackColors[forecastIndex] = item.backColors[index][0];
                    }
                }
            });

            // 更新对象的属性
            item.finalDate = finalDateArray?.map((item)=>{
                return moment(item, finalFormat).format(finalRenderFormat); // 使用合并后的日期数组
            }); // 使用合并后的日期数组
            item.finalRecommendationValues = finalRecommendationValues; // 更新推荐值
            item.finalBackColors = finalBackColors; // 更新背景色
        });
    };

    useEffect(()=>{
        if(currentPhytophagyStressReqDataForDaily?.length >= 1){
            classifyDataByFirstDate(currentPhytophagyStressReqDataForDaily);
        }
    },[currentPhytophagyStressReqDataForDaily]);

    console.log('lanxue value currentPhytophagyStressReqDataForDaily',currentPhytophagyStressReqDataForDaily);
    return showPhytophagyStress ? (
        <div style={{overflow:'auto'}}>
            {/* Forecast And Historical */}
            <IF show={forecastEchartData?.length >= 1 || historicalEchartData?.length >= 1}>
                {/* Forecast Table */}
                <div className="forecast-table content-basic chart-or-table-top disease-table-container">
                    <div className="historical-chart-title common-title">
                        {/* {translateValue?.phytophagy_stress || 'Phytophagy Stress'} */}
                        {heatOrColdType === HeatOrColdStressRiskEnum.HEATSTRESSRISK
                            ? translateValue?.heat_stress_risk || 'Heat Stress Risk'
                            : translateValue?.cold_stress_risk || 'Cold Stress Risk'}
                    </div>

                    {/* show Forecast Chart */}
                    {
                        forecastEchartData?.length >= 1 &&
                    <ForecastEchart
                        id = {`forecastEchartRef-${forecastEchartData?.length}`}
                        echartData={forecastEchartData}
                        heatOrColdType={heatOrColdType}
                    />
                    }
                    {/* show Historical Chart */}
                    {
                        historicalEchartData?.length >= 1 &&
                    <HistoricalChart
                        id = {`historicalEchartRef-${historicalEchartData?.length}`}
                        echartData={historicalEchartData}
                        heatOrColdType={heatOrColdType}
                    />
                    }

                    {/* @* Color Legend *@ */}
                    <div className="new-color-legend">
                        <table className="color-legend-table">
                            <thead>
                                <tr className="color-first-tr">
                                    <td colSpan={6}>
                                        {translateValue?.color_legend || 'Color Legend'}
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                <IF
                                    show={
                                        heatOrColdType === HeatOrColdStressRiskEnum.HEATSTRESSRISK
                                    }
                                >
                                    <tr>
                                        <td>
                                            <span>
                                                {translateValue?.heat_stress || 'Heat Stress'} :{' '}
                                            </span>
                                        </td>
                                        <td>
                                            <div>{translateValue?.no_stress || 'No Stress'}</div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: HeatColor.LOW }}
                                            >
                                                {translateValue?.low || 'Low'}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: HeatColor.Med }}
                                            >
                                                {translateValue?.med || 'Med'}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: HeatColor.High }}
                                            >
                                                {translateValue?.high || 'High'}
                                            </div>
                                        </td>
                                    </tr>
                                </IF>
                                <IF
                                    show={
                                        heatOrColdType === HeatOrColdStressRiskEnum.COLDSTRESSRISK
                                    }
                                >
                                    <tr>
                                        <td>
                                            <span>
                                                {translateValue?.cold_stress || 'Cold Stress'} :{' '}
                                            </span>
                                        </td>
                                        <td>
                                            <div>{translateValue?.no_stress || 'No Stress'}</div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: ColdColor.LOW }}
                                            >
                                                {translateValue?.low || 'Low'}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: ColdColor.Med }}
                                            >
                                                {translateValue?.med || 'Med'}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: ColdColor.High }}
                                            >
                                                {translateValue?.high || 'High'}
                                            </div>
                                        </td>
                                    </tr>
                                </IF>
                            </tbody>
                        </table>
                    </div>
                </div>
            </IF>

            {/* Mixed Forecast And Historical */}
            <IF show={mixedEchartData?.length >= 1 }>
                {/* Forecast Table */}
                <div className="forecast-table content-basic chart-or-table-top disease-table-container">
                    <div className="historical-chart-title common-title">
                        {/* {translateValue?.phytophagy_stress || 'Phytophagy Stress'} */}
                        {heatOrColdType === HeatOrColdStressRiskEnum.HEATSTRESSRISK
                            ? translateValue?.heat_stress_risk || 'Heat Stress Risk'
                            : translateValue?.cold_stress_risk || 'Cold Stress Risk'} {` - ${translateValue?.forecast + ' ' + translateValue?.and + ' ' + translateValue?.historical}`}
                    </div>

                    {/* show Forecast Chart */}
                    {
                        mixedEchartData?.length >= 1 &&
                    <ForecastEchart
                        id = {`mixedEchartRef-${mixedEchartData?.length}`}
                        echartData={mixedEchartData}
                        heatOrColdType={heatOrColdType}
                    />
                    }

                    {/* @* Color Legend *@ */}
                    <div className="new-color-legend">
                        <table className="color-legend-table">
                            <thead>
                                <tr className="color-first-tr">
                                    <td colSpan={6}>
                                        {translateValue?.color_legend || 'Color Legend'}
                                    </td>
                                </tr>
                            </thead>
                            <tbody>
                                <IF
                                    show={
                                        heatOrColdType === HeatOrColdStressRiskEnum.HEATSTRESSRISK
                                    }
                                >
                                    <tr>
                                        <td>
                                            <span>
                                                {translateValue?.heat_stress || 'Heat Stress'} :{' '}
                                            </span>
                                        </td>
                                        <td>
                                            <div>{translateValue?.no_stress || 'No Stress'}</div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: HeatColor.LOW }}
                                            >
                                                {translateValue?.low || 'Low'}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: HeatColor.Med }}
                                            >
                                                {translateValue?.med || 'Med'}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: HeatColor.High }}
                                            >
                                                {translateValue?.high || 'High'}
                                            </div>
                                        </td>
                                    </tr>
                                </IF>
                                <IF
                                    show={
                                        heatOrColdType === HeatOrColdStressRiskEnum.COLDSTRESSRISK
                                    }
                                >
                                    <tr>
                                        <td>
                                            <span>
                                                {translateValue?.cold_stress || 'Cold Stress'} :{' '}
                                            </span>
                                        </td>
                                        <td>
                                            <div>{translateValue?.no_stress || 'No Stress'}</div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: ColdColor.LOW }}
                                            >
                                                {translateValue?.low || 'Low'}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: ColdColor.Med }}
                                            >
                                                {translateValue?.med || 'Med'}
                                            </div>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div
                                                className="color-square"
                                                style={{ backgroundColor: ColdColor.High }}
                                            >
                                                {translateValue?.high || 'High'}
                                            </div>
                                        </td>
                                    </tr>
                                </IF>
                            </tbody>
                        </table>
                    </div>
                </div>
            </IF>
        </div>
    ) : null;
};

export default TemplatePhytophagy;
